/* =====================================================================
   Ivy Row — Welcome wheel and member area (prefix whrw-)
   Sections: rewards-wheel (/pages/avantages), member-portal (/pages/compte),
   member-cta. Loaded deferred after wh-core.js (if present): wise.js,
   loaded synchronously at end of body, has already wired the wheel.

   Contract with wise.js (unmodified file):
   - wise.js remains the engine: click on #wheel-spin, random draw,
     rotation of #wheel-dial (style.transform "rotate(Xdeg)" + class
     is-spinning during the spin), display in #wheel-result (class is-on),
     localStorage state "wise_wheel_prize" { used, last }, won code in
     "wise_saved_promo".
   - This file adds no listener on #wheel-spin or on the data-promo-*
     hooks. It observes #wheel-dial and #wheel-result (MutationObserver)
     and only listens to its own buttons, by delegation on the section
     root (the click bubbling up from the spin button only authorises
     sound, it does not launch anything).
   Additions: spring-loaded pointer that clicks on each peg, sound and
   vibration (can be disabled; vibration off in reduced motion), winning
   segment highlighted, Copy button, apply code to basket
   (/discount/CODE?redirect=...), result announced to screen readers,
   focus and scroll to result, "Welcome wheel" strip in the member area.
   ES5 (+ native clipboard promise), no dependencies, idempotent.
   ===================================================================== */
(function () {
  'use strict';

  if (window.WHRW) return;

  var STATE_KEY = 'wise_wheel_prize';
  var PROMO_KEY = 'wise_saved_promo';
  var FX_KEY = 'whrw_fx';

  /* Pointer: damped spring (degrees and seconds). */
  var SPRING_K = 760;
  var SPRING_C = 30;
  var PEG_IMPULSE = 820;
  var MAX_DEFLECT = 24;

  var mqReduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var AudioCtor = window.AudioContext || window.webkitAudioContext || null;
  var canVibrate = typeof navigator.vibrate === 'function';
  var actx = null;
  var lastPegSound = 0;

  function noop() {}

  function report(err) {
    if (window.console && typeof window.console.error === 'function') window.console.error('[whrw]', err);
  }

  function safe(fn, arg) {
    try {
      return fn(arg);
    } catch (err) {
      report(err);
      return undefined;
    }
  }

  function all(selector, ctx) {
    var base = ctx || document;
    if (!base || typeof base.querySelectorAll !== 'function') return [];
    return Array.prototype.slice.call(base.querySelectorAll(selector));
  }

  function matches(el, selector) {
    if (!el || el.nodeType !== 1) return false;
    var fn = el.matches || el.webkitMatchesSelector || el.msMatchesSelector;
    return !!fn && fn.call(el, selector);
  }

  function closest(node, selector) {
    var el = node && node.nodeType === 1 ? node : node && node.parentNode;
    while (el && el.nodeType === 1) {
      if (matches(el, selector)) return el;
      el = el.parentNode;
    }
    return null;
  }

  function within(scope, selector) {
    var base = scope || document;
    var list = all(selector, base);
    if (matches(base, selector)) list.unshift(base);
    return list;
  }

  function trim(value) {
    return String(value == null ? '' : value).replace(/^\s+|\s+$/g, '');
  }

  function isReduced() {
    if (window.WH && typeof window.WH.reduced === 'boolean') return window.WH.reduced;
    return !!(mqReduce && mqReduce.matches);
  }

  /* ---------- Storage: same keys and read logic as wise.js ---------- */
  function lsGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }

  function lsSet(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* storage unavailable */ }
  }

  function lsRemove(key) {
    try { window.localStorage.removeItem(key); } catch (e) { /* storage unavailable */ }
  }

  function readState() {
    var raw = null;
    try { raw = JSON.parse(lsGet(STATE_KEY) || 'null'); } catch (e) { raw = null; }
    if (!raw || typeof raw !== 'object') return { used: 0, last: null };
    if (raw.label !== undefined) return { used: 1, last: raw };
    return { used: parseInt(raw.used, 10) || 0, last: raw.last || null };
  }

  /* Sound and vibration: enabled unless the user toggles them off. */
  function fxOn() {
    return lsGet(FX_KEY) !== 'off';
  }

  /* ---------- Sound: synthesised (Web Audio), no files to load ---------- */
  function audioUnlock() {
    if (!AudioCtor || !fxOn()) return;
    if (navigator.audioSession) {
      /* Mixes with other sounds and respects iPhone silent mode. */
      try { navigator.audioSession.type = 'ambient'; } catch (e) { /* not supported */ }
    }
    if (!actx) {
      try { actx = new AudioCtor(); } catch (e) { actx = null; return; }
    }
    if (actx.state === 'suspended' && typeof actx.resume === 'function') {
      var pending = actx.resume();
      if (pending && typeof pending.then === 'function') pending.then(noop, noop);
    }
  }

  function tone(freq, freqEnd, duration, volume, type, delay) {
    if (!actx || actx.state !== 'running' || !fxOn()) return;
    try {
      var t0 = actx.currentTime + (delay || 0);
      var osc = actx.createOscillator();
      var gain = actx.createGain();
      osc.type = type || 'sine';
      osc.frequency.setValueAtTime(freq, t0);
      if (freqEnd && freqEnd !== freq) osc.frequency.exponentialRampToValueAtTime(freqEnd, t0 + duration);
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(volume, t0 + 0.004);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
      osc.connect(gain);
      gain.connect(actx.destination);
      osc.start(t0);
      osc.stop(t0 + duration + 0.02);
    } catch (e) { /* sound is optional */ }
  }

  /* Peg click: sharper as the wheel slows down. */
  function pegSound(strength) {
    var now = Date.now();
    if (now - lastPegSound < 34) return;
    lastPegSound = now;
    tone(2200, 1200, 0.03, 0.035 + 0.06 * strength, 'triangle', 0);
  }

  function resultSound(kind) {
    if (kind === 'win') {
      tone(659.25, 0, 0.2, 0.07, 'sine', 0);
      tone(987.77, 0, 0.36, 0.07, 'sine', 0.13);
    } else if (kind === 'respin') {
      tone(784, 0, 0.22, 0.06, 'sine', 0);
    } else {
      tone(330, 247, 0.32, 0.05, 'sine', 0);
    }
  }

  function vibrate(pattern) {
    if (!canVibrate || !fxOn() || isReduced()) return;
    try { navigator.vibrate(pattern); } catch (e) { /* not supported */ }
  }

  /* ---------- Announcements, copy, labels ---------- */
  function announce(region, message) {
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(function () { region.textContent = message; }, 80);
  }

  function legacyCopy(text) {
    var area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.top = '0';
    area.style.left = '0';
    area.style.opacity = '0';
    area.style.fontSize = '16px';
    document.body.appendChild(area);
    area.focus();
    area.select();
    try { area.setSelectionRange(0, text.length); } catch (e) { /* legacy browser */ }
    var ok = false;
    try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
    document.body.removeChild(area);
    return ok;
  }

  function copyText(text, done) {
    var clip = navigator.clipboard;
    if (clip && typeof clip.writeText === 'function' && window.isSecureContext !== false) {
      clip.writeText(text).then(function () { done(true); }, function () { done(legacyCopy(text)); });
    } else {
      done(legacyCopy(text));
    }
  }

  function selectText(el) {
    if (!el || !window.getSelection || !document.createRange) return;
    try {
      var range = document.createRange();
      range.selectNodeContents(el);
      var selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    } catch (e) { /* nothing to do */ }
  }

  /* Temporarily change a button's label (e.g. "Copied"). */
  function flashLabel(btn, text, ms) {
    var label = btn.querySelector('[data-whrw-label]') || btn;
    if (btn.__whrwText == null) btn.__whrwText = label.textContent;
    label.textContent = text;
    window.clearTimeout(btn.__whrwTimer);
    btn.__whrwTimer = window.setTimeout(function () {
      label.textContent = btn.__whrwText;
      btn.classList.remove('is-done');
    }, ms || 2000);
  }

  function copyCode(btn, code, region, chip) {
    if (!btn || !code) return;
    var focused = document.activeElement;
    copyText(code, function (ok) {
      if (focused && focused !== document.activeElement && typeof focused.focus === 'function') {
        try { focused.focus({ preventScroll: true }); } catch (e) { focused.focus(); }
      }
      if (ok) {
        btn.classList.add('is-done');
        flashLabel(btn, 'Copied', 2000);
        announce(region, 'Code ' + code + ' copied.');
      } else {
        selectText(chip);
        flashLabel(btn, 'Selected', 2600);
        announce(region, 'Automatic copy failed: code ' + code + ' is selected, please copy it manually.');
      }
    });
  }

  /* Link that applies the code: /discount/CODE then the basket (or the
     shop if the basket was empty on page load). */
  function setupApply(link, root, code) {
    if (!link || !code) return;
    var count = parseInt(root.getAttribute('data-whrw-cart-count'), 10) || 0;
    var target = count > 0
      ? (root.getAttribute('data-whrw-cart') || '/cart')
      : (root.getAttribute('data-whrw-shop') || '/collections/all');
    var base = String(root.getAttribute('data-whrw-root') || '/').replace(/\/+$/, '');
    link.setAttribute('href', base + '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(target));
    var label = link.querySelector('[data-whrw-label]');
    var text = link.getAttribute(count > 0 ? 'data-label-cart' : 'data-label-shop');
    if (label && text) label.textContent = text;
  }

  /* ---------- Wheel (rewards-wheel section) ---------- */
  function angleOf(el) {
    var m = /rotate\(\s*(-?[\d.]+(?:e-?\d+)?)deg\s*\)/.exec((el && el.style.transform) || '');
    return m ? parseFloat(m[1]) : 0;
  }

  function mod(a, b) {
    return ((a % b) + b) % b;
  }

  function parsePrizes(root) {
    var el = root.querySelector('#wheel-data');
    if (!el) return [];
    try {
      var list = JSON.parse(el.textContent || '[]');
      return Object.prototype.toString.call(list) === '[object Array]' ? list : [];
    } catch (e) {
      return [];
    }
  }

  function initWheel(root) {
    if (root.__whrw) return;
    var api = { destroy: noop, preview: noop };
    root.__whrw = api;

    var wheel = root.querySelector('.whrw-wheel');
    var dial = root.querySelector('[data-whrw-dial]');
    var pointer = root.querySelector('[data-whrw-pointer]');
    var segs = all('[data-whrw-seg]', dial || root);
    var labels = all('.whrw-label[data-whrw-label]', dial || root);
    var cases = all('[data-whrw-index]', root);
    var spin = root.querySelector('#wheel-spin');
    var result = root.querySelector('[data-whrw-result]');
    var titleEl = root.querySelector('#wheel-result-title');
    var chip = root.querySelector('#wheel-result-code');
    var kicker = root.querySelector('[data-whrw-kicker]');
    var copyBtn = root.querySelector('[data-whrw-copy]');
    var actions = root.querySelector('[data-whrw-actions]');
    var apply = root.querySelector('[data-whrw-apply]');
    var fine = root.querySelector('[data-whrw-fine]');
    var live = root.querySelector('[data-whrw-live]');
    var fxBtn = root.querySelector('[data-whrw-fx]');
    var prizes = parsePrizes(root);
    var count = segs.length;
    var segAngle = count ? 360 / count : 0;
    var hasMO = typeof window.MutationObserver === 'function';

    var observers = [];
    var timers = [];
    var raf = 0;
    var lastT = 0;
    var lastA = 0;
    var lastTick = null;
    var spinning = false;
    var phi = 0;
    var vel = 0;
    var resultOn = false;
    var current = null;

    /* Sound button: fixed name ("Sound"), state exposed via aria-pressed and icon. */
    function syncFx() {
      if (!fxBtn) return;
      var on = fxOn();
      fxBtn.classList.toggle('is-off', !on);
      fxBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
    }

    if (fxBtn) {
      if (AudioCtor || canVibrate) fxBtn.hidden = false;
      syncFx();
    }

    /* Highlight a segment (-1 = none). */
    function highlight(idx) {
      root.classList.toggle('is-landed', idx > -1);
      segs.forEach(function (el, i) {
        el.classList.toggle('is-win', i === idx);
        el.classList.toggle('is-dim', idx > -1 && i !== idx);
      });
      labels.forEach(function (el, i) {
        el.classList.toggle('is-win', i === idx);
        el.classList.toggle('is-dim', idx > -1 && i !== idx);
      });
    }

    /* ----- Pointer and pegs ----- */
    function kick() {
      if (!raf) raf = window.requestAnimationFrame(frame);
    }

    function onPeg(dir, speed) {
      vel -= dir * PEG_IMPULSE;
      pegSound(Math.max(0.2, Math.min(1, 1 - speed / 1800)));
      if (speed < 420) vibrate(6);
    }

    function frame(ts) {
      raf = 0;
      var dt = lastT ? Math.min(0.034, Math.max(0.001, (ts - lastT) / 1000)) : 1 / 60;
      lastT = ts;

      if (spinning && dial && segAngle) {
        var a = angleOf(dial);
        var tick = Math.floor(a / segAngle);
        if (lastTick === null) lastTick = tick;
        if (tick !== lastTick) {
          onPeg(tick > lastTick ? 1 : -1, Math.abs(a - lastA) / dt);
          lastTick = tick;
        }
        lastA = a;
      }

      if (pointer && !isReduced()) {
        /* Fixed 1/240 s steps: same feel at 60, 90 or 120 fps. */
        var steps = Math.max(1, Math.min(10, Math.round(dt * 240)));
        var h = dt / steps;
        for (var s = 0; s < steps; s++) {
          vel += (-SPRING_K * phi - SPRING_C * vel) * h;
          phi += vel * h;
          if (phi < -MAX_DEFLECT) {
            phi = -MAX_DEFLECT;
            if (vel < 0) vel = 0;
          }
          if (phi > MAX_DEFLECT) {
            phi = MAX_DEFLECT;
            if (vel > 0) vel = 0;
          }
        }
        pointer.style.transform = 'rotate(' + phi.toFixed(2) + 'deg)';
      }

      if (spinning || Math.abs(phi) > 0.05 || Math.abs(vel) > 1) {
        kick();
      } else {
        phi = 0;
        vel = 0;
        lastT = 0;
        if (pointer) pointer.style.transform = '';
      }
    }

    /* Mobile: if the wheel is off-screen when spinning, scroll it into view. */
    function showWheel() {
      if (!wheel || typeof wheel.getBoundingClientRect !== 'function') return;
      var rect = wheel.getBoundingClientRect();
      var viewport = window.innerHeight || document.documentElement.clientHeight;
      var top = rect.top - wheel.offsetHeight * 0.06;
      /* 58 px: fixed theme header; 62 px: mobile tab bar */
      if (top < 58 || rect.bottom > viewport - 62) {
        try {
          wheel.scrollIntoView({ behavior: isReduced() ? 'auto' : 'smooth', block: 'center' });
        } catch (e) {
          wheel.scrollIntoView();
        }
      }
    }

    function startSpin() {
      spinning = true;
      lastTick = null;
      lastT = 0;
      lastA = angleOf(dial);
      root.classList.add('is-spinning');
      highlight(-1);
      announce(live, 'The wheel is spinning.');
      showWheel();
      kick();
    }

    function stopSpin() {
      spinning = false;
      root.classList.remove('is-spinning');
    }

    /* ----- Result ----- */
    function findIndex(code, label) {
      var i;
      var wanted = code ? String(code).toUpperCase() : '';
      if (wanted) {
        for (i = 0; i < prizes.length; i++) {
          if (prizes[i] && prizes[i].code && String(prizes[i].code).toUpperCase() === wanted) return i;
        }
      }
      if (label) {
        for (i = 0; i < prizes.length; i++) {
          if (prizes[i] && prizes[i].label === label) return i;
        }
      }
      return -1;
    }

    function kindOf(prize) {
      if (!prize) return '';
      if (prize.code) return 'win';
      return prize.lose ? 'lose' : 'respin';
    }

    /* Scroll the result into view (mobile: it sits below the wheel). */
    function revealResult() {
      if (!result || typeof result.getBoundingClientRect !== 'function') return;
      var rect = result.getBoundingClientRect();
      var viewport = window.innerHeight || document.documentElement.clientHeight;
      if (rect.top < 58 || rect.bottom > viewport - 72) {
        try {
          result.scrollIntoView({ behavior: isReduced() ? 'auto' : 'smooth', block: 'center' });
        } catch (e) {
          result.scrollIntoView();
        }
      }
    }

    function showResult(restored) {
      var state = readState();
      var shown = titleEl ? trim(titleEl.textContent) : '';
      var idx = -1;
      var prize = null;

      if (!restored && dial && segAngle) {
        /* Segment under the pointer, from the final angle set by wise.js. */
        idx = Math.floor(mod(360 - mod(angleOf(dial), 360), 360) / segAngle);
        if (idx >= count) idx = count - 1;
        if (!prizes[idx] || (shown && prizes[idx].label !== shown)) idx = findIndex('', shown);
        prize = idx > -1 ? prizes[idx] : null;
      } else {
        prize = state.last;
        idx = prize ? findIndex(prize.code, prize.label) : findIndex('', shown);
      }

      var code = prize && prize.code ? trim(prize.code) : '';
      if (!code && chip && !chip.hidden && !prize) code = trim(chip.textContent);
      var kind = code ? 'win' : (kindOf(prize) || 'respin');
      current = { kind: kind, code: code, idx: idx };

      if (result) result.setAttribute('data-whrw-kind', kind);
      if (kicker) kicker.textContent = kind === 'win' ? 'You won!' : (kind === 'respin' ? 'Free respin' : 'Result');
      if (copyBtn) copyBtn.hidden = !code;
      if (actions) actions.hidden = !code;
      if (fine) fine.hidden = !code;
      if (code) setupApply(apply, root, code);

      if (restored) {
        /* Spin already played: the wheel returns to the winning segment only if wise.js locked the button.
           Otherwise (spins remaining), its internal angle stays at 0: no segment highlighted, the list and result suffice. */
        if (spin && spin.disabled && dial && idx > -1 && segAngle) {
          dial.style.transform = 'rotate(' + (360 - idx * segAngle - segAngle / 2).toFixed(3) + 'deg)';
          highlight(idx);
        } else {
          highlight(-1);
        }
        return;
      }
      highlight(idx);

      resultSound(kind);
      vibrate(kind === 'win' ? [18, 60, 32] : (kind === 'respin' ? 14 : 30));

      var label = prize && prize.label ? prize.label : shown;
      if (kind === 'win') {
        announce(live, 'You won: ' + label + '. Your code: ' + code + '. It has been saved for your basket.');
      } else if (kind === 'respin') {
        announce(live, label + ': this spin does not count, you can spin again.');
      }

      var active = document.activeElement;
      var lostFocus = !active || active === document.body || (spin && active === spin && spin.disabled);
      if (lostFocus && result) {
        try { result.focus({ preventScroll: true }); } catch (e) { result.focus(); }
      }
      timers.push(window.setTimeout(revealResult, isReduced() ? 0 : 650));
    }

    function clearResult() {
      current = null;
      if (result) result.removeAttribute('data-whrw-kind');
      if (kicker) kicker.textContent = '';
      if (copyBtn) copyBtn.hidden = true;
      if (actions) actions.hidden = true;
      if (fine) fine.hidden = true;
      highlight(-1);
    }

    /* ----- Editor: selected block = highlighted segment ----- */
    function preview(idx) {
      if (current) return;
      highlight(idx);
      cases.forEach(function (el) {
        el.classList.toggle('is-preview', parseInt(el.getAttribute('data-whrw-index'), 10) === idx);
      });
    }

    /* ----- Clicks: only our own buttons (delegation) ----- */
    function onClick(e) {
      var target = e.target;
      if (spin && closest(target, '#wheel-spin') === spin) {
        /* The spin is launched by wise.js; this click only authorises sound. */
        audioUnlock();
        return;
      }
      var fx = closest(target, '[data-whrw-fx]');
      if (fx && root.contains(fx)) {
        lsSet(FX_KEY, fxOn() ? 'off' : 'on');
        syncFx();
        if (fxOn()) audioUnlock();
        return;
      }
      var copy = closest(target, '[data-whrw-copy]');
      if (copy && root.contains(copy)) {
        copyCode(copy, current && current.code, live, chip);
        return;
      }
      var reset = closest(target, '[data-whrw-reset]');
      if (reset && root.contains(reset)) {
        lsRemove(STATE_KEY);
        lsRemove(PROMO_KEY);
        window.location.reload();
      }
    }

    root.addEventListener('click', onClick);

    if (hasMO && dial && spin) {
      var dialObserver = new window.MutationObserver(function () {
        var on = dial.classList.contains('is-spinning');
        if (on && !spinning) startSpin();
        else if (!on && spinning) stopSpin();
      });
      dialObserver.observe(dial, { attributes: true, attributeFilter: ['class'] });
      observers.push(dialObserver);
    }

    if (hasMO && result) {
      var resultObserver = new window.MutationObserver(function () {
        var on = result.classList.contains('is-on');
        if (on && !resultOn) {
          resultOn = true;
          safe(showResult, false);
        } else if (!on && resultOn) {
          resultOn = false;
          clearResult();
        }
      });
      resultObserver.observe(result, { attributes: true, attributeFilter: ['class'] });
      observers.push(resultObserver);
    }

    /* Result already displayed by wise.js on load (spin played earlier). */
    if (result && result.classList.contains('is-on')) {
      resultOn = true;
      safe(showResult, true);
    }

    function destroy() {
      observers.forEach(function (o) { o.disconnect(); });
      observers = [];
      timers.forEach(function (t) { window.clearTimeout(t); });
      timers = [];
      root.removeEventListener('click', onClick);
      if (raf) window.cancelAnimationFrame(raf);
      raf = 0;
      root.__whrw = null;
    }

    api.destroy = destroy;
    api.preview = preview;
  }

  /* ---------- "Welcome wheel" strip (member-portal, logged-in customer) ---------- */
  function initStrip(root) {
    if (root.__whrw) return;
    var api = { destroy: noop };
    root.__whrw = api;

    var invite = root.querySelector('[data-whrw-strip-invite]');
    var box = root.querySelector('[data-whrw-strip-code]');
    var chip = root.querySelector('[data-whrw-strip-chip]');
    var desc = root.querySelector('[data-whrw-strip-desc]');
    var apply = root.querySelector('[data-whrw-apply]');
    var live = root.querySelector('[data-whrw-live]');
    var allowed = parseInt(root.getAttribute('data-whrw-allowed'), 10) || 1;
    var code = '';
    var observer = null;

    /* Invitation while the spin has not been played, won code afterwards, nothing after a losing spin. */
    function render() {
      var state = readState();
      var last = state.last;
      if (last && last.code) {
        code = trim(last.code);
        if (chip) chip.textContent = code;
        if (desc) desc.textContent = last.desc || last.label || '';
        setupApply(apply, root, code);
        if (invite) invite.hidden = true;
        if (box) box.hidden = false;
        root.hidden = false;
      } else if (state.used >= allowed) {
        code = '';
        root.hidden = true;
      } else {
        code = '';
        if (invite) invite.hidden = false;
        if (box) box.hidden = true;
        root.hidden = false;
      }
    }

    function onClick(e) {
      var btn = closest(e.target, '[data-whrw-copy]');
      if (btn && root.contains(btn)) copyCode(btn, code, live, chip);
    }

    function onStorage(e) {
      if (!e || e.key === null || e.key === STATE_KEY) render();
    }

    render();
    root.addEventListener('click', onClick);
    window.addEventListener('storage', onStorage);

    /* Wheel on the "My rewards" tab (wise.js) on the same page: follow its result without subscribing. */
    var legacy = document.getElementById('wheel-result');
    if (legacy && !closest(legacy, '[data-whrw-wheel]') && typeof window.MutationObserver === 'function') {
      observer = new window.MutationObserver(function () {
        if (legacy.classList.contains('is-on')) render();
      });
      observer.observe(legacy, { attributes: true, attributeFilter: ['class'] });
    }

    api.destroy = function () {
      if (observer) observer.disconnect();
      window.removeEventListener('storage', onStorage);
      root.removeEventListener('click', onClick);
      root.__whrw = null;
    };
  }

  /* ---------- Initialisation ---------- */
  function initAll(scope) {
    within(scope, '[data-whrw-wheel]').forEach(function (el) { safe(initWheel, el); });
    within(scope, '[data-whrw-strip]').forEach(function (el) { safe(initStrip, el); });
  }

  function destroyIn(scope) {
    within(scope, '[data-whrw-wheel], [data-whrw-strip]').forEach(function (el) {
      if (el.__whrw && typeof el.__whrw.destroy === 'function') safe(el.__whrw.destroy);
    });
  }

  function onBlock(e, selected) {
    var target = e.target;
    var root = closest(target, '[data-whrw-wheel]');
    if (!root || !root.__whrw || typeof root.__whrw.preview !== 'function') return;
    var idx = parseInt(target && target.getAttribute ? target.getAttribute('data-whrw-index') : '', 10);
    root.__whrw.preview(selected && !isNaN(idx) ? idx : -1);
  }

  document.addEventListener('shopify:block:select', function (e) { onBlock(e, true); });
  document.addEventListener('shopify:block:deselect', function (e) { onBlock(e, false); });

  window.WHRW = { version: '1.0.0', init: initAll };

  if (window.WH && typeof window.WH.ready === 'function') {
    window.WH.ready(initAll);
    if (typeof window.WH.unload === 'function') window.WH.unload(destroyIn);
  } else {
    var boot = function () { initAll(document); };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
    document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
    document.addEventListener('shopify:section:unload', function (e) { destroyIn(e.target); });
  }
})();
